import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../stores/auth';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { VERSION } from '../lib/version';
import { TENANT_NAME } from '../lib/tenant';

export default function Login() {
  const [, setLocation] = useLocation();
  const { login, mustChangePassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login(email, password);

      // Check mustChangePassword from the store after login
      const authState = useAuth.getState();
      if (authState.mustChangePassword) {
        setLocation('/first-access');
      } else {
        setLocation('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center p-4 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated orbs */}
        <div
          className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-float"
          style={{ background: 'hsl(var(--accent-cyan) / 0.3)' }}
        />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-float"
          style={{ background: 'hsl(var(--accent-violet) / 0.3)', animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl animate-pulse"
          style={{ background: 'hsl(var(--primary) / 0.2)' }}
        />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div
        className={`relative w-full max-w-sm transition-all duration-700 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-3xl text-white text-4xl font-bold shadow-2xl animate-float bg-gradient-primary"
            style={{
              animationDuration: '4s',
              background: 'linear-gradient(135deg, hsl(217 91% 60%), hsl(224 76% 48%))',
              boxShadow: '0 0 30px hsl(var(--primary) / 0.4)',
            }}
          >
            <span className="drop-shadow-lg">P</span>
          </div>
          <h1 className="text-3xl font-bold text-white drop-shadow-lg">Prisma Mobile</h1>
          <p className="text-blue-200/80 font-medium">Trade Marketing</p>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-8 rounded-3xl shadow-2xl border border-white/20"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {error && (
            <div className="rounded-2xl p-4 text-sm text-white animate-shake border border-red-500/30"
                 style={{ background: 'rgba(239, 68, 68, 0.2)' }}>
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-semibold text-white/90">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
              disabled={isLoading}
              className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                disabled:opacity-50 transition-all duration-300"
              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-semibold text-white/90">
              Senha
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                disabled={isLoading}
                className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                  focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                  disabled:opacity-50 transition-all duration-300 pr-12"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-1"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="relative w-full overflow-hidden rounded-2xl py-4 font-bold text-blue-700
              disabled:opacity-50
              flex items-center justify-center gap-2 transition-all duration-300
              active:scale-[0.98] group"
            style={{
              background: 'linear-gradient(135deg, #ffffff, #e0e7ff)',
              boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
            }}
          >
            {/* Shimmer effect */}
            <div
              className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              }}
            />

            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="relative">Entrando...</span>
              </>
            ) : (
              <span className="relative">Entrar</span>
            )}
          </button>
        </form>

        {/* Forgot Password Link */}
        <div className="mt-4 text-center space-y-2">
          <button
            type="button"
            onClick={() => setLocation('/forgot-password')}
            className="text-sm text-white/70 hover:text-white transition-colors font-medium"
          >
            Esqueci minha senha
          </button>
          <div>
            <button
              type="button"
              onClick={() => setLocation('/first-access')}
              className="text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              Primeiro acesso?
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white/50">
          Prisma by {TENANT_NAME} | v{VERSION.gitCommitShort} ({VERSION.gitBranch})
        </p>
      </div>
    </div>
  );
}
